import { useKeyboardOpen } from './lib/useKeyboardOpen';
/** Оболочка приложения: загрузка, шапка, выбор экрана, тосты и офлайн. */
import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ErrorState, Loading } from './components/States';
import { ProjectWatchers, MontageProgress, activeProjectIds } from './components/ProjectActivity';
import { TabBar } from './components/TabBar';
import type { Tab } from './components/TabBar';
import * as telegram from './lib/telegram';
import { cleanupStaleUploads } from './lib/upload';
import { useActions } from './state/actions';
import { deriveScreen } from './state/screen';
import { identifyUser, trackScreen } from './lib/analytics';
import { useStore } from './state/store';
import { t, useLang } from './i18n';
import type { Screen } from './state/store';
import { Balance } from './screens/Balance';
import { Editor } from './screens/Editor';
import { Invite } from './screens/Invite';
import { Onboarding } from './screens/Onboarding';
import { Profile } from './screens/Profile';
import { Progress } from './screens/Progress';
import { Projects } from './screens/Projects';
import { Result } from './screens/Result';
import { Support } from './screens/Support';

/** Вкладка нижней панели для экрана; онбординг показывается без панели. */
function activeTab(screen: Screen): Tab | null {
  switch (screen) {
    case 'invite':
      return 'invite';
    case 'projects':
    case 'progress':
    case 'result':
      return 'projects';
    case 'editor':
      return 'new';
    case 'balance':
    case 'limit':
      return 'balance';
    case 'profile':
    case 'support':
      return 'profile';
    default:
      return null;
  }
}

/**
 * Куда ведёт кнопка «назад» Telegram: у вкладок её нет, из поддержки —
 * в профиль, из потока монтажа — к списку проектов.
 */
function backTarget(screen: Screen): Screen | null {
  if (screen === 'support') return 'profile';
  if (screen === 'onboarding' || activeTab(screen) !== null) return null;
  return 'projects';
}

function ScreenBody({ screen }: { screen: Screen }): ReactElement {
  switch (screen) {
    case 'onboarding':
      return <Onboarding />;
    case 'editor':
      return <Editor />;
    case 'progress':
      return <Progress />;
    case 'result':
      return <Result />;
    case 'invite':
      return <Invite />;
    case 'projects':
      return <Projects />;
    case 'profile':
      return <Profile />;
    case 'support':
      return <Support />;
    case 'balance':
      return <Balance />;
    case 'limit':
      return <Balance exhausted />;
    default:
      return <Editor />;
  }
}

/**
 * Прогресс монтажа и вкладки внизу экрана. Высоту берём из вёрстки, а не из
 * констант: по ней главные кнопки и тосты встают над панелью (--dock-h).
 */
function BottomDock({ children }: { children: ReactNode }): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const root = document.documentElement;
    const measure = (): void => root.style.setProperty('--dock-h', node.offsetHeight + 'px');
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    measure();
    return () => { observer.disconnect(); root.style.removeProperty('--dock-h'); };
  }, []);
  return <div ref={ref} className="bottom-dock">{children}</div>;
}

export function App(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const booted = useRef(false);
  const keyboardOpen = useKeyboardOpen();
  // Смена языка перемонтирует экраны (ключ), а bootstrap остаётся: App не пересоздаётся.
  const lang = useLang();

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    telegram.init();
    cleanupStaleUploads();
    void actions.bootstrap();
  }, [actions]);

  useEffect(() => {
    const online = (): void => dispatch({ type: 'online', value: true });
    const offline = (): void => dispatch({ type: 'online', value: false });
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!state.toast) return;
    const timer = window.setTimeout(() => dispatch({ type: 'toast', message: null }), 4000);
    return () => window.clearTimeout(timer);
  }, [dispatch, state.toast]);

  const screen = deriveScreen(state);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [screen]);
  useEffect(() => { if (!state.booting) trackScreen(screen); }, [screen, state.booting]);
  const userId = state.profile?.user.id;
  const username = state.profile?.user.username ?? null;
  useEffect(() => { if (userId) identifyUser(userId, username); }, [userId, username]);

  // Аппаратная кнопка «назад» в Telegram.
  useEffect(() => {
    const target = backTarget(screen);
    telegram.backButton.set(target ? () => dispatch({ type: 'route', screen: target }) : null);
  }, [dispatch, screen]);

  const tab = activeTab(screen);
  const showTabs = tab !== null && !state.booting && !state.bootError;

  const selectTab = (next: Tab): void => {
    if (next === 'new') {
      // Черновик остаётся в редакторе, готовый или идущий проект уступает место новому.
      if (state.project && state.project.status !== 'draft') {
        dispatch({ type: 'project/set', project: null });
        dispatch({ type: 'uploads/clear' });
      }
      dispatch({ type: 'route', screen: 'editor' });
      return;
    }
    dispatch({ type: 'route', screen: next });
  };

  return (
    <div key={lang} className={'app' + (showTabs ? ' with-tabs' : '')}>
      {!state.booting && !state.bootError ? <ProjectWatchers /> : null}
      <main className={'content' + (screen === 'editor' ? ' editor-design' : '')}>
        {state.booting ? <Loading rows={3} text={t('app.booting')} /> : null}

        {!state.booting && state.bootError ? (
          <ErrorState
            message={state.bootError}
            onRetry={() => {
              booted.current = true;
              void actions.bootstrap();
            }}
          />
        ) : null}

        {!state.booting && !state.bootError ? <ScreenBody screen={screen} /> : null}
      </main>

      {state.toast ? (
        <div className="toast" role="status">
          {state.toast}
        </div>
      ) : null}

      {showTabs && !keyboardOpen && tab !== null ? <BottomDock>
        <MontageProgress />
        <TabBar active={tab} onSelect={selectTab} processing={activeProjectIds(state).length > 0} readyCount={state.unreadProjects.length} />
      </BottomDock> : null}
    </div>
  );
}
